import { useState } from "react";
import { ActivityIndicator, Pressable } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Button } from "@src/components/Button";
import { Icon } from "@src/components/Icon";
import type { UseCameraTestSignalLogResult } from "@visioni/a3s-monitoring/app";
import { EMPTY_LOG_MESSAGE, toLogRow, type TestSignalLogRow, type TestSignalLogTone } from "../log-rows";

interface TestSignalLogProps {
  /**
   * Owned by the page rather than by this list: the buttons, the watch panel
   * and this list all read one hook instance, because a press has to start a
   * watch that another part of the page renders.
   */
  log: UseCameraTestSignalLogResult;
}

const REFRESH_ICON_SIZE = 16;
/** Reserved whether or not the spinner is showing, so the button never moves. */
const REFRESH_SPINNER_SLOT = 20;

/**
 * Compact local-time label, matching the Snapshots and confirmed-dumps lists
 * (`15 Sept, 14:58`). The endpoint returns raw ISO precisely so the viewer's own
 * zone is what is shown — no site timezone is plumbed to the server.
 */
function formatOccurredAt(iso: string): string {
  return new Date(iso).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * The same instant in full, for the hover detail: weekday, date, seconds, and
 * the zone — without which an operator comparing this against a device log or a
 * Patriot record cannot tell which offset the compact label was rendered in.
 */
function formatOccurredAtFull(iso: string): string {
  return new Date(iso).toLocaleString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZoneName: "longOffset",
  });
}

function toneColor(tone: TestSignalLogTone, theme: ReturnType<typeof useUnistyles>["theme"]): string {
  const colors = {
    positive: theme.colors.status.success,
    negative: theme.colors.status.error,
    pending: theme.colors.status.warning,
    neutral: theme.colors.textMuted,
  } as const satisfies Record<TestSignalLogTone, string>;
  return colors[tone];
}

/** One `Label: value` pair. Labelling each field is what makes a dense row scannable. */
function Field({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <View flexDirection="row" alignItems="baseline" gap="xs">
      <Text textStyle="xs/regular" color="textMuted">
        {label}:
      </Text>
      <Text textStyle="sm/regular" {...(color ? { style: { color } } : { color: "text" as const })}>
        {value}
      </Text>
    </View>
  );
}

/**
 * The timestamp, revealing the full instant on hover.
 *
 * Hover only — so in practice this is web. Native has no hover, and the compact
 * label it falls back to is the same one every other list in the app shows, so
 * nothing is lost there.
 */
function Timestamp({ iso }: { iso: string }) {
  const { theme } = useUnistyles();
  const [hovered, setHovered] = useState(false);

  return (
    <View>
      <Pressable onHoverIn={() => setHovered(true)} onHoverOut={() => setHovered(false)}>
        <View flexDirection="row" alignItems="baseline" gap="xs">
          <Text textStyle="xs/regular" color="textMuted">
            Time:
          </Text>
          <Text textStyle="sm/regular" color="text">
            {formatOccurredAt(iso)}
          </Text>
        </View>
      </Pressable>
      {hovered && (
        <View
          // Floats above the row rather than widening it, so revealing the
          // detail does not reflow the list under the pointer.
          style={{ position: "absolute", bottom: "100%", left: 0, zIndex: 1 }}
          bg="surfaceCard"
          borderWidth={1}
          borderColor="borderCard"
          borderRadius={theme.spacing.sm}
          px="lg"
          py="md"
          mb="xs"
        >
          <Text textStyle="xs/regular" color="text" numberOfLines={1}>
            {formatOccurredAtFull(iso)}
          </Text>
        </View>
      )}
    </View>
  );
}

function LogRow({ row, isFirst }: { row: TestSignalLogRow; isFirst: boolean }) {
  const { theme } = useUnistyles();

  return (
    <View
      p="3xl"
      gap="sm"
      // Separator between rows only, so the card's own border is not doubled.
      borderTopWidth={isFirst ? 0 : 1}
      borderColor="borderCard"
    >
      <View flexDirection="row" flexWrap="wrap" alignItems="baseline" gap="2xl">
        <Field label="Kind" value={row.entry} />
        <Field label="Path" value={row.signalPath} />
        <Timestamp iso={row.occurredAt} />
      </View>
      {/* One line per stage. A signal's cloud half is separate from its
          delivery half because a dropped delivery is not a failed signal —
          see `TestSignalLogRow.statuses`. */}
      {row.statuses.map((status) => (
        <Field key={status.label} label={status.label} value={status.text} color={toneColor(status.tone, theme)} />
      ))}
      {row.detail !== null && (
        <Text textStyle="xs/regular" color="textMuted">
          {row.detail}
        </Text>
      )}
    </View>
  );
}

/**
 * A camera's test signal history: what an operator asked for, and what the
 * pipeline did with the signal it produced, newest first.
 *
 * Answers what the buttons cannot: whether a signal reached the monitoring
 * centre or was dropped on the way. Whether one is still expected belongs to
 * the watch panel above — this renders only rows that exist.
 */
export function TestSignalLog({ log }: TestSignalLogProps) {
  const { theme } = useUnistyles();

  const rows = log.entries.map(toLogRow);

  return (
    <View gap="xl">
      <View flexDirection="row" alignItems="center" justifyContent="space-between" gap="xl">
        <Text textStyle="sm/semibold" color="text">
          Recent activity
        </Text>
        {/* Outside a watch the log does not poll, and this is how the operator
            goes looking for a signal.

            The spinner is outside the button and the label never changes, so
            the button cannot move under a pointer already aimed at it. */}
        <View flexDirection="row" alignItems="center" gap="md">
          <View style={{ width: REFRESH_SPINNER_SLOT }} alignItems="center">
            {log.isFetching && <ActivityIndicator size="small" color={theme.colors.textMuted} />}
          </View>
          <Button
            label="Refresh"
            variant="secondary"
            size="sm"
            onPress={log.refresh}
            disabled={log.isFetching}
            iconLeft={<Icon name="refresh" size={REFRESH_ICON_SIZE} color={theme.colors.text} />}
          />
        </View>
      </View>

      <View bg="surfaceCardInner" borderRadius={theme.spacing.lg} borderWidth={1} borderColor="borderCard">
        {log.isLoading ?
          <View p="3xl">
            <Text textStyle="sm/regular" color="textMuted">
              Loading…
            </Text>
          </View>
        : log.isError ?
          <View p="3xl">
            <Text textStyle="sm/regular" color="danger">
              {log.error?.message ?? "Could not load the test signal log."}
            </Text>
          </View>
        : rows.length === 0 ?
          <View p="3xl">
            <Text textStyle="sm/regular" color="textMuted">
              {EMPTY_LOG_MESSAGE}
            </Text>
          </View>
        : rows.map((row, index) => <LogRow key={row.key} row={row} isFirst={index === 0} />)}
      </View>

      {/* Always rendered, disabled when there is nothing more: a control that
          appears and disappears as you page is harder to aim at than one that
          simply greys out. A button rather than infinite scroll keeps it
          keyboard-reachable.

          `secondary` rather than `link` because it is usually disabled, and a
          greyed-out link is indistinguishable from plain text. */}
      <View alignItems="flex-start">
        <Button
          label="Load more"
          variant="secondary"
          size="sm"
          onPress={log.fetchNextPage}
          disabled={!log.hasNextPage || log.isFetchingNextPage}
          loading={log.isFetchingNextPage}
          loadingText="Loading…"
        />
      </View>
    </View>
  );
}
